import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const ManualCardModal = ({ open, onClose, onSaveSuccess, API_BASE }) => {
  const [serverError, setServerError] = useState("");

  // Validation Schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("Full Name is required"),
    email: Yup.string()
      .email("Enter a valid email address")
      .required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9+\s-]{8,20}$/, "Enter a valid phone number")
      .required("Phone number is required"),
    title: Yup.string(),
    company: Yup.string(),
    website: Yup.string().url("Enter a valid URL (include https://)"),
    address: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      title: "",
      company: "",
      phone: "",
      email: "",
      website: "",
      address: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setServerError("");
      try {
        const response = await axios.post(
          `${API_BASE}/save-visiting-card`,
          values,
          { withCredentials: true }
        );

        if (response.data.success) {
          resetForm();
          onSaveSuccess(); // Refresh list in parent
          onClose(); // Close modal
        }
      } catch (err) {
        setServerError(err.response?.data?.message || "Failed to save card.");
      }
    },
  });

  // Handle modal close and reset formik state
  const handleClose = () => {
    formik.resetForm();
    setServerError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
        Add Manual Visiting Card
      </DialogTitle>
      
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          {serverError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverError}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name *"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <TextField
              fullWidth
              label="Email Address *"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />

            <TextField
              fullWidth
              label="Phone Number *"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />

            <TextField
              fullWidth
              label="Job Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />

            <TextField
              fullWidth
              label="Company Name"
              name="company"
              value={formik.values.company}
              onChange={formik.handleChange}
            />

            <TextField
              fullWidth
              label="Website (e.g., https://example.com)"
              name="website"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
            />

            <TextField
              fullWidth
              label="Address"
              name="address"
              multiline
              rows={2}
              value={formik.values.address}
              onChange={formik.handleChange}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} color="inherit" disabled={formik.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            sx={{
              backgroundColor: "#6366F1",
              px: 3,
              "&:hover": { backgroundColor: "#4F46E5" },
            }}
          >
            {formik.isSubmitting ? "Saving..." : "Save Card"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManualCardModal;